import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from '../../store/hooks';
import { setAuth, clearAuth } from '../../store/slices/authSlice';
import useApi from '../useApi';
import { AUTH_ENDPOINTS } from './auth.endpoints';

const clearAuthAndRedirect = (dispatch, navigate) => {
  localStorage.clear();
  sessionStorage.clear();
  dispatch(clearAuth());
  navigate('/login', { replace: true });
};

/**
 * Login - POST /auth/login
 * execute({ email, password })
 */
export const useLogin = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  return useApi(AUTH_ENDPOINTS.login, {
    method: 'POST',
    errorMessage: 'Login failed',
    onSuccess: (response) => {
      const { accessToken, refreshToken, user } = response.data;

      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('refreshToken', refreshToken);
      localStorage.setItem('user', JSON.stringify(user));
      dispatch(setAuth(user));
      sessionStorage.setItem('justLoggedIn', 'true');
      navigate('/splash');
    },
  });
};

export const useLogout = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  return useApi(AUTH_ENDPOINTS.logout, {
    method: 'POST',
    errorMessage: 'Logout failed',
    onSuccess: () => clearAuthAndRedirect(dispatch, navigate),
    onError: () => clearAuthAndRedirect(dispatch, navigate),
  });
};

export const useLogoutAll = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  return useApi(AUTH_ENDPOINTS.logoutAll, {
    method: 'POST',
    errorMessage: 'Failed to logout from all devices',
    onSuccess: () => clearAuthAndRedirect(dispatch, navigate),
    onError: () => clearAuthAndRedirect(dispatch, navigate),
  });
};

export const useRefreshToken = () =>
  useApi(AUTH_ENDPOINTS.refresh, {
    method: 'POST',
    errorMessage: 'Failed to refresh token',
    transformResponse: (res) => res?.data ?? res,
  });

export const useMe = () =>
  useApi(AUTH_ENDPOINTS.me, {
    method: 'GET',
    immediate: true,
    errorMessage: 'Failed to fetch profile',
    transformResponse: (res) => res?.data?.user ?? res?.user ?? null,
  });

export const useForgotPassword = () =>
  useApi(AUTH_ENDPOINTS.forgotPassword, {
    method: 'POST',
    errorMessage: 'Failed to send password reset email',
    transformResponse: (res) => res?.data ?? res,
  });


  export const useVerifyOtp = () =>
  useApi(AUTH_ENDPOINTS.verifyOtp, {
    method: 'POST',
    errorMessage: 'Invalid or expired OTP',
    transformResponse: (res) => res?.data ?? res,
  });

export const useResetPassword = () =>
  useApi(AUTH_ENDPOINTS.resetPassword, {
    method: 'POST',
    errorMessage: 'Failed to reset password',
    transformResponse: (res) => res?.data ?? res,
  });

export const useUpdatePassword = () =>
  useApi(AUTH_ENDPOINTS.updatePassword, {
    method: 'POST',
    errorMessage: 'Failed to update password',
    transformResponse: (res) => res?.data ?? res,
  });

export const useSessionsList = () =>
  useApi(AUTH_ENDPOINTS.sessions, {
    method: 'GET',
    immediate: true,
    errorMessage: 'Failed to fetch sessions',
    transformResponse: (res) => res?.data?.sessions ?? [],
  });


export const useRevokeSession = () => {
  const api = useApi(
    (sessionId) => AUTH_ENDPOINTS.sessionById(sessionId),
    { method: 'DELETE', errorMessage: 'Failed to revoke session' }
  );

  const execute = async (sessionId) => {
    if (!sessionId) throw new Error('Session ID is required');
    return api.execute(sessionId);
  };

  return { execute, loading: api.loading, error: api.error };
};
